import { createFileRoute } from "@tanstack/react-router";
import { useParams } from "~/components/navigation";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { Button } from "~/components/Button";
import { useDictation } from "~/components/useDictation";
import { useLocalStorage } from "~/components/useLocalStorage";
import { trpc } from "~/utils/trpc.client";
import { StudentFileUploadButton } from "~/app/(new)/[teamId]/(with-student-nav)/students/[studentId]/StudentFileUploadButton";
import toast from "react-hot-toast";
import { errHandler } from "~/utils/clientLogger";
import { FormContext, KeyedItemWrapperMemo } from "~/components/UiForm";
import { FieldType, Form, KeyedData } from "~/components/UiForm.types";
import { z } from "zod";
import { useCallbackRef } from "~/components/useCallbackRef";
import { FilesDataProvider } from "~/components/useFilesData";
import { AutoExpandingTextArea } from "~/components/AutoExpandingTextArea";
import { Modal } from "~/components/Modal";
import CalendarEmbed from "~/app/(new)/[teamId]/playground/CalendarEmbed";

const formIndices: number[] = [];

export const Route = createFileRoute("/_authed/$teamId/playground")({
  component: PlaygroundPage,
});

function PlaygroundPage() {
  const params = useParams<{ teamId: string }>();
  const students = trpc.students.getStudents.useQuery({
    teamId: params.teamId,
  });

  const studentId =
    students.data?.find((s) => s.name === "Playground Student")?.id ?? "";

  const files = trpc.files.getFileDataForStudent.useQuery(
    { studentId },
    {
      enabled: studentId.length > 0,
    },
  );

  const refetchFiles = files.refetch;

  const updateFile = trpc.files.updateFile.useMutation();

  const sendInterest = trpc.clientLog.sendInterest.useMutation();

  const [backgroundInputMode, setBackgroundInputMode] = useLocalStorage(
    "backgroundInputMode",
    "", // empty, "voice", "type", or "demo"
  );
  const [formConfig, setFormConfig] = useLocalStorage(
    "formConfig",
    z
      .object({
        region: z
          .enum(["Hawaii", "NYC", "LA County", "Northern CA (Bay Area)"])
          .default("Hawaii"),
      })
      .prefault({}),
  );

  const [backgroundText, setBackgroundText] = useLocalStorage(
    "backgroundText",
    "",
  );

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [formData, setFormData] = useLocalStorage(
    "formData",
    z
      .object({
        backgroundInformation: z.string().default(""),
        plop: z.string().default(""),
        goals: z
          .array(
            z.object({
              goalArea: z.string().default(""),
              goal: z.string().default(""),
            }),
          )
          .default([{ goalArea: "", goal: "" }]),
      })
      .prefault({}),
  );

  const getFormData = useCallbackRef(() => formData);

  const copyDemoFilesToStudent =
    trpc.files.copyDemoFilesToStudent.useMutation();

  const generatePresignedUrl =
    trpc.files.generatePresignedUrlForFile.useMutation();

  const { startRecording, stopRecording, isRecording } = useDictation({
    onResult: (result) => {
      setBackgroundText((prev) => `${prev.trim()} ${result.trim()}`);
    },
    onEnd: () => {
      // noop
    },
  });

  const form = useMemo(
    (): Form => ({
      title: "Playground Form",
      description: "This is a playground form",
      data: {},
      items:
        formConfig.region === "NYC"
          ? [
              {
                id: "plep",
                type: FieldType.MULTI_LINE_TEXT,
                label: "Current Performance and Needs",
                description:
                  "Documentation of scholar's current performance and academic, developmental, and functional needs.",
                helpText:
                  "Include demographic information, the grade level, areas of concern, previous relevant educational information, and biographical information. Do not include the students birthday or specific test scores. The output should be one paragraph.",
                llmHelpText:
                  "Be concise and direct. Do not use any fluffy or extraneous language.",
              },
              {
                id: "academicPerformance",
                type: FieldType.MULTI_LINE_TEXT,
                label: "Academic Performance",
                keyQuestionsLabel: "Include information about:",
                keyQuestions: [
                  "Reading strengths (decoding, fluency, comprehension)",
                  "Reading needs (decoding, fluency, comprehension)",
                  "Reading interventions & scaffolds and their effectiveness",
                  "Writing strengths (writing, spelling, organization)",
                  "Writing needs (writing, spelling, organization)",
                  "Writing interventions & scaffolds and their effectiveness",
                  "Math strengths (calculation, problem solving)",
                  "Math needs (calculation, problem solving)",
                  "Math interventions & scaffolds and their effectiveness",
                  "Science strengths (if applicable)",
                  "Science needs (if applicable)",
                ],
                description:
                  "Include a summary of the student's academic performance in sections of Reading, Writing, Math, and Science (if applicable). Each section should include strengths, needs, and interventions/scaffolds (where applicable).",
                helpText:
                  "Use bullet points for each item. If the student is performing near, at, or above grade level in any area, indicate that no supports are needed for that area. This section should only contain information related to academic performance.",
                llmHelpText:
                  "Do not use any fluffy or extraneous language. Format this as plain text and NOT in markdown, do not use * or #s.",
              },
              {
                id: "goals",
                type: FieldType.LIST,
                label: "Goals",
                description: "The goals for the student.",
                addMoreLabel: "Another Goal",
                items: [
                  {
                    id: "goalArea",
                    type: FieldType.TEXT,
                    label: "Goal Area",
                    placeholder: "Language Arts, Mathematics, etc.",
                  },
                  {
                    id: "goal",
                    type: FieldType.MULTI_LINE_TEXT,
                    label: "Measurable Annual Goal",
                    description:
                      "A SMART goal for the student in the goal area. Ensure the goal is Specific, Measurable, Achievable, Relevant, and Time-bound.",
                    llmHelpText:
                      "The goal area is {{ data.goals[i[0]].goalArea | required: 'goal area' }}.",
                  },
                ],
              },
            ]
          : // formConfig.region === "Hawaii" ?
            [
              {
                id: "eligibilityCategoryAndAreasOfConcern",
                type: FieldType.MULTI_LINE_TEXT,
                label: "Eligibility Category and Areas of Concern",
                description:
                  "A single paragraph describing the student's eligibility category and areas of concern, including demographic information, grade level, and areas of concern.",
                helpText:
                  "Omit the student's birthday and specific test scores in this section.",
              },
              {
                id: "readingPhonics",
                type: FieldType.MULTI_LINE_TEXT,
                label: "Reading: Phonics",
                keyQuestionsLabel: "Include information about:",
                keyQuestions: [
                  "Results from reading phonics assessments",
                  "Previous IEP goals and progress monitoring data",
                  "Student's current abilities in this area",
                  "Specific needs, teaching strategies and accommodations",
                  "Parent input regarding reading phonics skills",
                ],
                description:
                  "Include a summary of the student's reading phonics skills in sections of Diagnostic Assessment, Progress Monitoring Assessment, Previous Data, Standard, and Parent Input and Concerns.",
                llmHelpText: `
        Here is the format for the response, section by section:

        # Diagnostic Assessment:

        Describe results from one of the following:
        - Core Phonics Survey
        - Hasbrouck Phonics Survey
        - LETRS Phonics and Word Reading Survey
        Include dates and specific scores. Be concise and direct.

        If no relevant assessment data exists, output: N/A - No phonics assessment data available

        # Progress Monitoring Assessment:

        If DIBELS Nonsense Word Fluency is used, include the results with dates and scores.

        If no progress monitoring data exists, output: N/A - No progress monitoring data available

        # Previous Data:

        Summarize previous IEP goals related to phonics and current progress monitoring data. Use this format: 'On the last IEP, (insert student name) had goals and objectives for (insert phonics goals/objectives). Based on his/her progress monitoring data, (insert current progress)'

        If no previous data exists, output: N/A - No previous IEP goals or progress monitoring data available

        # Standard:

        State the grade-level Common Core expectations for phonics and word recognition skills for the student's grade.

        (insert student name) is able to:
        (3-6 bullet points of current abilities based on assessment data)

        (insert student name) needs to:
        (Bullet points covering specific learning needs, teaching strategies needed, and required accommodations/assistive technology)

        # Parent Input and Concerns:
        If provided, include relevant parent input specific to phonics. Otherwise, output: No parent input

        Only include information directly related to phonics. Write in plain text without markdown formatting, using standard bullet points.`,
              },
              {
                id: "mathematics",
                type: FieldType.MULTI_LINE_TEXT,
                label: "Mathematics",
                required: true,
                keyQuestionsLabel: "Include information about:",
                keyQuestions: [
                  "Results from math assessments",
                  "Previous IEP goals and progress monitoring data",
                  "Student's current abilities in this area",
                  "Specific needs, teaching strategies and accommodations",
                  "Parent input regarding math skills",
                ],
                description:
                  "Include a summary of the student's math skills in sections of Diagnostic Assessment, Progress Monitoring Assessment, Previous Data, Standard, and Parent Input and Concerns.",
                llmHelpText: `

        Here is the format for the response, section by section:

        # Diagnostic Assessment:

        Describe the iReady Math Assessment results including:
        - Date Administered
        - Scaled Score
        - Grade Level Equivalent
        - Brief description of the assessment and how it was administered
        - Comparison to grade level expectations

        Include dates and specific scores. Be concise and direct.

        If no relevant assessment data exists, output: N/A - No mathematics assessment data available

        # Progress Monitoring Assessment:

        If Listening to Learn Math Interview or other math assessments were used, include:
        - Date administered
        - Type of assessment
        - Specific findings and scores
        - Comparison to grade level expectations

        If no progress monitoring data exists, output: N/A - No progress monitoring data available

        # Previous Data:

        Summarize previous IEP goals related to mathematics and current progress monitoring data. Use this format: 'On the last IEP, (insert student name) had goals and objectives for (insert mathematics goals/objectives). Based on his/her progress monitoring data, (insert current progress)'

        If no previous data exists, output: N/A - No previous IEP goals or progress monitoring data available

        # Standard:

        State the grade-level Common Core expectations for mathematics skills for the student's grade.

        (insert student name) is able to:
        (3-6 bullet points of current abilities based on assessment data)

        (insert student name) needs to:
        (Bullet points covering specific learning needs, teaching strategies needed, and required accommodations/assistive technology)

        # Parent Input and Concerns:
        If provided, include relevant parent input specific to mathematics. Otherwise, output: No parent input

        Only include information directly related to mathematics. Write in plain text without markdown formatting, using standard bullet points.`,
              },
              {
                id: "functionalSocial",
                type: FieldType.MULTI_LINE_TEXT,
                label: "Functional Performance: Social/Behavioral",
                keyQuestionsLabel: "Include information about:",
                keyQuestions: [
                  "Structured behavioral observation data",
                  "Previous behavioral goals and progress",
                  "Function of behavior analysis",
                  "Antecedents and consequences",
                  "Current positive behavioral interventions",
                  "Parent input regarding behavioral concerns",
                ],
                description:
                  "Include a summary of the student's social/behavioral skills in sections of Diagnostic Assessment, Progress Monitoring Assessment, Previous Data, Standard, and Parent Input and Concerns.",
                llmHelpText: `
        Here is the format for the response:

        # Previous Data:

        On the last IEP, (insert student name) had goals and objectives for (insert behavior goals/objectives). Based on progress monitoring data, (insert current progress)

        # Behavioral Observations:

        Include structured observation data with dates, frequency, and duration of behaviors. Describe the function of behavior and its relationship to the environment.

        (insert student name) demonstrates these behavioral strengths:
        (3-6 bullet points of positive behavioral characteristics)

        (insert student name) demonstrates these behavioral needs:
        (Bullet points including:
        - Operational definition of problem behaviors
        - Antecedents and consequences
        - Function of behavior
        - Required positive behavioral interventions and supports
        - Specific strategies and accommodations needed)

        # Parent Input and Concerns:

        If provided, include relevant parent input specific to behavioral concerns

        Only include if there are social/behavioral concerns. Write in plain text without markdown formatting, using standard bullet points.`,
              },
              {
                id: "goals",
                type: FieldType.LIST,
                label: "Goals",
                addMoreLabel: "Another Goal",
                items: [
                  {
                    id: "goalArea",
                    label: "Goal Area",
                    description:
                      "The area of this IEP goal, such as Reading, Mathematics, etc.",
                    type: FieldType.TEXT,
                    placeholder: "Reading, Mathematics, etc.",
                  },
                  {
                    id: "goal",
                    label: "Measurable Annual Goal",
                    type: FieldType.MULTI_LINE_TEXT,
                    description:
                      "A SMART goal for the student in the goal area. Ensure the goal is Specific, Measurable, Achievable, Relevant, and Time-bound.",
                    llmHelpText: `
          Use clear, jargon-free language that can be understood by all readers without any markdown formatting or special characters.

          The goal area is {{ data.goals[i[0]].goalArea | required: 'goal area' }}.

          Each goal must:
          1. Address a specific need identified in the PLEP
          2. Start with 'By the end of the IEP year,'
          3. Include the specific setting (e.g., 'in a one-to-one setting', 'in the resource room', 'in the general education classroom')
          4. Specify conditions (e.g., 'given 1 minute to read', 'when presented with grade-level text')
          5. Include specific, measurable targets
          6. State the frequency or number of opportunities to demonstrate mastery
          7. After a line break, include baseline data in this format:
          Baseline:
          [Assessment]
          [Measure] - [Current Score] and [Accuracy]

          Baseline data should come directly from an assessment of students current level of performance. If no available data, then output 'N/A'. Check to make sure data is coming from the assessment data.
          `,
                  },
                ],
              },
            ],
    }),
    [formConfig],
  );

  const formContextValue = useMemo(() => {
    return {
      setItemData: setFormData as Dispatch<SetStateAction<unknown>>,
      getFormData,
      setFormData: setFormData as Dispatch<unknown>,
      form,
      registerIntersectionElement: () => {
        // noop
      },
    };
  }, [setFormData, form, getFormData]);

  const [hasUsedDemoFiles, setHasUsedDemoFiles] = useLocalStorage(
    "hasUsedDemoFiles",
    z.boolean().default(false),
  );

  // use an effect to refresh the files when there is a file without a summary or without a new name
  useEffect(() => {
    if (files.data) {
      const filesWithoutSummary = files.data.filter((f) => !f.summary);
      const filesWithoutNewName = files.data.filter(
        (f) => f.name === f.originalName,
      );
      if (filesWithoutSummary.length > 0 || filesWithoutNewName.length > 0) {
        const timeout = setTimeout(() => {
          void refetchFiles();
        }, 5000);
        return () => {
          clearTimeout(timeout);
        };
      }
    }
  }, [files.data, refetchFiles]);

  const [isShowingDemoVideo, setIsShowingDemoVideo] = useState(false);

  // if there's no playground student, then show an error
  if (students.data && !studentId) {
    return (
      <div className="w-full flex flex-col items-center justify-center gap-4 py-8">
        <div className="card bg-base-100 shadow max-w-xl w-full">
          <div className="card-body">
            <h1 className="text-2xl font-bold">SimpleIEP Playground</h1>
            <p className="text-sm opacity-80">
              No playground student found.
              <br />
              Contact us at{" "}
              <a href="mailto:hello@simple-iep.com" className="link">
                hello@simple-iep.com
              </a>{" "}
              to get started.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col items-center justify-center gap-8 py-8">
      <div className="card bg-base-100 shadow max-w-xl w-full">
        <div className="card-body gap-8">
          <div className="text-2xl font-bold">👋 Welcome to SimpleIEP!</div>
          <div className="text-sm ">
            SimpleIEP is an AI-powered platform that makes the entire IEP
            process faster, easier, and more effective.
          </div>
          <ul className="pl-7 flex flex-col gap-2 [&>li]:before:content-['✅'] [&>li]:before:mr-2 [&>li]:before:-ml-7 list-none list-inside text-sm">
            <li>
              Generate IEPs customized to your school or district’s format
            </li>
            <li>
              Use AI to craft SMART goals, track progress, and ensure compliance
            </li>
            <li>Export IEPs to Google Docs, PDFs, and more</li>
            <li>Manage multiple students effortlessly</li>
            <li>Enjoy FERPA and HIPAA-compliant data security</li>
          </ul>
          <div className="text-sm">
            <span className="font-bold opacity-90">🎉 Try Our Demo:</span>{" "}
            Experience how fast and easy it is to create a professional,
            compliant IEP. Our demo will walk you through creating an IEP using
            either real data you upload or sample documents we provide.
          </div>

          {/* video, only show on desktop because mobile will see it in the next section */}
          {isShowingDemoVideo ? (
            <video
              src="https://s3.us-west-2.amazonaws.com/cdn.simple-iep.com/SimpleIEP-Demo.mp4"
              controls
              autoPlay
            ></video>
          ) : (
            <button
              className="btn btn-ghost w-full"
              onClick={() => {
                setIsShowingDemoVideo(true);
              }}
            >
              Prefer to watch a video?
            </button>
          )}
        </div>
      </div>

      {/* how it works */}
      <div className="card bg-base-100 shadow max-w-xl w-full">
        <div className="card-body gap-4">
          <h2 className="text-2xl font-bold w-full">How It Works</h2>
          <div className="text-sm">
            💡
            <span className="italic">
              Creating an IEP with SimpleIEP is quick and easy—this process
              takes about <span className="font-bold">5 minutes</span>!
            </span>
          </div>
          <div className="flex flex-col gap-2">
            <h3 className="font-bold opacity-90">
              Step 1: Upload Existing Documentation
            </h3>
            <div className="text-sm">
              Provide any existing documentation about the student. This
              information helps personalize the IEP and ensures the AI generates
              relevant recommendations.
            </div>
            <h3 className="font-bold opacity-90 pt-4">
              Step 2: Share Updates on the Student
            </h3>
            <div className="text-sm">
              Add recent progress, behaviors, and performance details. Keeping
              this information up-to-date ensures the IEP reflects the student’s
              current abilities and goals, which is crucial for effective
              planning and interventions.
            </div>
            <h3 className="font-bold opacity-90 pt-4">
              Step 3: Customize Your IEP Format
            </h3>
            <div className="text-sm">
              Select the sections most relevant to your needs (e.g., Background
              Information, Present Levels, Goals). This demo uses a generic IEP
              format, but when your school adopts SimpleIEP, we fully customize
              it to match your district’s requirements and preferences.
            </div>
          </div>

          <div className="text-sm">
            🌟
            <span className="italic">
              <span className="font-bold">Reminder:</span> Take your time
              exploring! Try out different sections and features to see just how
              powerful and flexible SimpleIEP is.
            </span>
          </div>
        </div>
      </div>

      {/* Message for mobile */}
      <div className="card bg-base-100 shadow max-w-xl w-full md:hidden block">
        <div className="card-body">
          <h2 className="text-2xl font-bold w-full text-center">
            ☎️ Hey there!
          </h2>
          <p className="text-sm opacity-80">
            Writing IEPs is best done on a computer or tablet. You won{"'"}t get
            the whole experience on your phone, so maybe try later or watch this
            video.
          </p>
          <div className="flex justify-center">
            <video
              src="https://s3.us-west-2.amazonaws.com/cdn.simple-iep.com/SimpleIEP-Demo.mp4"
              controls
            ></video>
          </div>
        </div>
      </div>

      {/* existing documentation */}
      <div className="card bg-base-100 shadow max-w-xl w-full">
        <div className="card-body">
          <div className="text-lg opacity-80">Step 1 of 3</div>
          <h2 className="text-2xl font-bold">Upload Existing Documentation</h2>
          <p className="text-sm">
            Provide any existing documentation about the student. This
            information helps personalize the IEP and ensures the AI generates
            relevant and tailored recommendations.
          </p>
          <h3 className="font-bold opacity-90 pt-4">
            📂 Helpful Documents to Upload
          </h3>
          <ul className="list-disc list-inside pl-4 text-sm">
            <li>Previous IEPs</li>
            <li>Test scores and assessment results</li>
            <li>Psychological evaluations</li>
            <li>Behavior and progress reports</li>
            <li>Parental and teacher feedback</li>
          </ul>
          <div className="flex gap-2 items-center justify-center pt-4">
            <StudentFileUploadButton
              studentId={studentId}
              teamId={params.teamId}
            >
              <Button className="btn btn-primary">Upload files</Button>
            </StudentFileUploadButton>
            {files.data?.length === 0 || !hasUsedDemoFiles ? (
              <>
                <div className="divider divider-horizontal opacity-50 texts-sm">
                  or
                </div>
                <Button
                  className="btn btn-secondary"
                  loading={copyDemoFilesToStudent.isPending}
                  onClick={() => {
                    copyDemoFilesToStudent
                      .mutateAsync({
                        studentId,
                      })
                      .then(() => {
                        setHasUsedDemoFiles(true);
                      })
                      .catch(errHandler("copying demo files"));
                  }}
                >
                  Use demo files
                </Button>
              </>
            ) : null}
          </div>
          <div className="flex gap-2 items-center justify-center text-center">
            <ul className="text-xs opacity-70">
              <li>All files are encrypted and stored securely.</li>
              <li>SimpleIEP is FERPA and HIPAA compliant.</li>
              <li>No data is ever used for training AI models.</li>
            </ul>
          </div>

          <div className="flex flex-col gap-2">
            {files.data && files.data.length > 0 ? (
              <div className="text-lg font-bold opacity-80 mt-4">
                Uploaded files
              </div>
            ) : null}
            {files.data?.map((f) => (
              <div
                key={f.id}
                className="bg-base-200 p-3 rounded flex flex-col shadow"
              >
                <div className="grid grid-cols-[1fr_max-content] gap-2 items-baseline">
                  <div>
                    <div className="text-sm font-bold opacity-80">{f.name}</div>
                    {f.name !== f.originalName ? (
                      <div className="text-xs opacity-80">{f.originalName}</div>
                    ) : null}
                  </div>
                  <div className="flex">
                    <Button
                      onClick={() => {
                        generatePresignedUrl
                          .mutateAsync({
                            fileId: f.id,
                          })
                          .then((url) => {
                            window.open(url, "_blank");
                          })
                          .catch(errHandler("generating presigned url", f.id));
                      }}
                      className="btn btn-ghost btn-sm btn-circle"
                    >
                      <span className="ph ph-arrow-square-out"></span>
                    </Button>
                    <Button
                      className="btn btn-ghost btn-sm btn-circle"
                      onClick={() => {
                        updateFile
                          .mutateAsync({
                            id: f.id,
                            isArchived: true,
                          })
                          .then(() => {
                            toast.success("File archived");
                          })
                          .catch(errHandler("archiving file"));
                      }}
                    >
                      <span className="ph ph-trash"></span>
                    </Button>
                  </div>
                </div>
                <details className="text-xs opacity-80 group">
                  <summary
                    className="cursor-pointer group-open:line-clamp-none
                    line-clamp-3"
                  >
                    {f.summary}
                  </summary>
                </details>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* card 2 */}
      <div className="card bg-base-100 shadow max-w-xl w-full">
        <div className="card-body">
          <div className="text-lg opacity-80">Step 2 of 3</div>
          <h2 className="text-2xl font-bold">Give Updates About the Student</h2>
          <p className="text-sm">
            Share key updates to ensure the IEP reflects the student’s current
            progress and needs. The more detail you provide, the more
            personalized and effective the IEP will be.
          </p>

          <h3 className="font-bold opacity-90 pt-4">
            📝 What Types of Updates Should You Include?
          </h3>
          <ul className="list-disc list-inside pl-4 text-sm">
            <li>Areas of need or recent challenges</li>
            <li>Test performance or assessment results</li>
            <li>Classroom observations or behavioral notes</li>
          </ul>

          <h3 className="font-bold opacity-90 pt-4">
            🎙️ Speak Freely, We’ll Handle the Rest
          </h3>
          <p className="text-sm">
            Our platform leverages advanced voice recognition technology, so you
            can skip the typing! Simply talk casually—like you’re updating a
            colleague about the student—and let SimpleIEP capture your insights
            accurately. Simply click “Start Recoding” below.
          </p>

          <div className="flex gap-2 items-center justify-center flex-col md:flex-row">
            <button
              className={`btn btn-primary ${isRecording ? "btn-error animate-pulse" : ""}`}
              onClick={() => {
                if (isRecording) {
                  stopRecording();
                } else {
                  startRecording();
                }
                setBackgroundInputMode("voice");
              }}
            >
              {isRecording
                ? "Stop recording"
                : backgroundInputMode === "voice"
                  ? "Start recording"
                  : "Use voice"}
            </button>
            <div className="divider md:divider-horizontal opacity-50 texts-sm">
              or
            </div>
            <button
              disabled={isRecording}
              className="btn btn-secondary"
              onClick={() => {
                setBackgroundInputMode("type");
              }}
            >
              Type
            </button>
            <div className="divider md:divider-horizontal opacity-50 texts-sm">
              or
            </div>
            <button
              disabled={isRecording}
              className="btn btn-secondary"
              onClick={() => {
                setBackgroundInputMode("demo");
                setBackgroundText(
                  "Adrian has had a good year advancing in his reading skills. He is now reading with more confidence. Math is still a strong subject for him. Behavior has been hit or miss. He has been working on his social skills and has made some friends.",
                );
              }}
            >
              Use demo text
            </button>
          </div>

          {backgroundInputMode === "voice" ? (
            <>
              <div className="flex flex-col gap-2 bg-base-200 p-3 rounded shadow">
                {backgroundText}
              </div>
              {backgroundText.length === 0 ? (
                <div className="text-sm italic opacity-80">
                  Start talking to type...
                </div>
              ) : null}
            </>
          ) : null}
          {backgroundInputMode === "type" ? (
            <div className="flex flex-col gap-2">
              <AutoExpandingTextArea
                className="textarea textarea-bordered"
                value={backgroundText}
                onChange={(e) => {
                  setBackgroundText(e.target.value);
                }}
              />
            </div>
          ) : null}
          {backgroundInputMode === "demo" ? (
            <div className="flex flex-col gap-2">
              <div className="bg-base-200 p-3 rounded shadow">
                {backgroundText}
              </div>
            </div>
          ) : null}
          <h3 className="font-bold opacity-90 pt-4">
            🛠️ Troubleshooting Voice Recording
          </h3>
          <div className="text-sm">
            If voice recording isn’t working, check that your browser’s
            microphone permissions are enabled. If the issue persists, try the
            typing or demo feature instead so you can keep exploring the
            platform! feature instead so you can keep exploring the platform!
          </div>
        </div>
      </div>

      {/* Customize IEP format */}
      <div className="card bg-base-100 shadow max-w-xl w-full">
        <div className="card-body">
          <div className="text-lg opacity-80">Step 3 of 3</div>
          <h2 className="text-2xl font-bold">Customize Your IEP Format</h2>
          <p className="text-sm">
            For this demo, we’ve provided a few templates to help you see how
            SimpleIEP can adapt to different formats and goal types. Feel free
            to play around with the options and see how they impact the IEP
            output.
          </p>

          <h3 className="font-bold opacity-90 pt-4">🌎 Available Templates</h3>
          <p className="text-sm">
            Select a region below to tailor your output.
          </p>
          <div className="flex gap-2">
            {[
              "Hawaii" as const,
              "NYC" as const,
              // These aren't ready yet
              // "LA County" as const,
              // "Northern CA (Bay Area)" as const,
            ].map((region) => (
              <button
                key={region}
                onClick={() => {
                  setFormConfig((prev) => {
                    return {
                      ...prev,
                      region,
                    };
                  });
                }}
                className={`rounded-full btn btn-sm ${
                  formConfig.region === region ? "btn-primary" : "btn-secondary"
                }`}
              >
                {region}
              </button>
            ))}
          </div>

          <h3 className="font-bold opacity-90 pt-4">
            🏫 Custom Templates for Your School
          </h3>
          <div className="text-sm">
            When your school adopts SimpleIEP, we’ll create a fully customized
            version tailored to match your specific IEP format. Every section,
            from Background Information to Goals, will align perfectly with your
            school or district’s requirements.
          </div>

          <h3 className="font-bold opacity-90 pt-4">✏️ Write with AI</h3>
          <div className="text-sm">
            Using the information you’ve provided, SimpleIEP’s AI generates
            professional, compliant IEP sections like Present Levels of
            Performance (PLOP) and goals. This feature combines your data,
            updates, and preferences to create a personalized IEP that’s ready
            for review.
          </div>

          <h3 className="font-bold opacity-90 pt-4 text-sm">
            How to Use the Tools
          </h3>

          <div className="grid grid-cols-[max-content_1fr] gap-4 items-center">
            <div className="text-sm">
              <div className="btn btn-sm btn-circle pointer-events-none">
                <span className="ph ph-microphone"></span>
              </div>
            </div>
            <div className="text-sm">
              Provide updates or edits with voice. Pretend you are talking to a
              colleague about the student.
              <div>
                e.g.{" "}
                <span className="italic">
                  James&apos;s reading went from 3rd to 4th grade level last
                  semester
                </span>
              </div>
              <div>
                e.g.{" "}
                <span className="italic">
                  Make this more formal and only a single paragraph
                </span>
              </div>
            </div>
            <div className="text-sm">
              <button className="btn btn-sm btn-circle btn-primary pointer-events-none">
                <span className="ph ph-play"></span>
              </button>
            </div>
            <div className="text-sm">
              Generate a full output based on all the information you’ve shared.
              The AI will pull everything together into a polished IEP section.
            </div>
          </div>
        </div>
      </div>

      {/* Write with AI */}
      <div className="card bg-base-100 shadow max-w-3xl w-full">
        <div className="card-body">
          <h2 className="text-2xl font-bold">Write with AI</h2>
          <p className="text-sm opacity-80">
            We use the information you provided to help you while writing your
            IEP. Try with the three sections below!
          </p>
          <div className="flex flex-col gap-2 w-full">
            <FilesDataProvider studentId={studentId}>
              <FormContext.Provider value={formContextValue}>
                <div
                  className={`flex w-full flex-col gap-2 [&_#goals]:bg-transparent [&_#goals]:shadow-none`}
                >
                  {form.items.map((item, index) => {
                    return (
                      <KeyedItemWrapperMemo
                        key={index}
                        path={item.id}
                        childItem={item}
                        parentItemData={formData}
                        setParentItemData={
                          setFormData as Dispatch<SetStateAction<KeyedData>>
                        }
                        formIndices={formIndices}
                      />
                    );
                  })}
                </div>
              </FormContext.Provider>
            </FilesDataProvider>
          </div>
        </div>
      </div>

      {/* Data Visualization */}
      <div className="card bg-base-100 shadow max-w-xl w-full">
        <div className="card-body">
          <h2 className="text-2xl font-bold">See Progress at a Glance</h2>
          <div className="text-sm">
            SimpleIEP also includes progress tracking and visualizations to help
            understand how students are doing over time.
          </div>

          <div className="mockup-window bg-base-300 shadow-lg my-8">
            <img src={"/goals.jpg"} alt="Data Visualization" width={600} />
          </div>
          <h3>Key Features</h3>
          <ul className="list-disc list-inside text-sm opacity-80 pl-4">
            <li>Visual insights into performance</li>
            <li>Early identification of trends</li>
            <li>Easy adjustments to goals or interventions</li>
          </ul>
        </div>
      </div>

      {/* Next steps */}
      <div className="card bg-base-100 shadow max-w-xl w-full">
        <div className="card-body">
          <h2 className="text-2xl font-bold">🚀 What next?</h2>
          <div className="text-sm opacity-80">
            When your school or district adopts SimpleIEP, you can use this same
            tool to write EVERY SECTION of your IEP and related forms. You will
            also be able to:
          </div>
          <ul className="list-disc list-inside text-sm opacity-80 pl-4">
            <li>100% custom IEP that matches your format exactly</li>
            <li>Custom AI for each section of the IEP</li>
            <li>Export to Google Docs, PDF, and more</li>
            <li>Integrate with your other student systems</li>
            <li>Manage multiple students</li>
            <li>Track goals in SimpleIEP</li>
          </ul>
          <a
            href="https://youtu.be/1mheOAmqxgI"
            className="link text-sm"
            target="_blank"
            rel="noreferrer"
          >
            Watch a full demo video to see all of the features.
          </a>
          <h3 className="font-bold text-lg pt-8 w-full text-center">
            💡 Ready to Transform IEPs at Your School?
          </h3>
          <div className="w-full flex justify-center">
            <button
              className="btn btn-lg bg-gradient-to-r from-orange-600 via-fuchsia-500 to-indigo-600 text-white !rounded"
              onClick={() => {
                setIsModalOpen(true);
                sendInterest.mutate();
              }}
            >
              Get Started
            </button>
          </div>
        </div>
      </div>

      {/* Floating Call to Action */}
      <div className="absolute bottom-3 right-12">
        <Modal
          isOpen={isModalOpen}
          setIsOpen={setIsModalOpen}
          trigger={
            <button
              className="btn btn-sm !rounded-full bg-gradient-to-r from-orange-600 via-fuchsia-500 to-indigo-600 text-white"
              onClick={() => {
                sendInterest.mutate();
              }}
            >
              Get Started
            </button>
          }
        >
          <div className="bg-base-100 p-8 rounded-lg flex flex-col gap-2 w-[90dvw] max-w-7xl items-center">
            <div className="max-w-lg flex flex-col gap-4">
              <h2 className="text-2xl font-bold opacity-90 w-full text-center">
                🚀 Get Started 🚀
              </h2>
              <div className="">
                We&apos;ve let the SimpleIEP team know you&apos;re interested in
                getting started. They will reach out to you to schedule a call,
                or you can choose a time to meet below.
              </div>
            </div>

            <div className="w-full h-full py-8">
              <CalendarEmbed />
            </div>

            <div className="">
              If you have any questions, please reach out to us at{" "}
              <a
                href="mailto:hello@simple-iep.com"
                className="link"
                target="_blank"
                rel="noreferrer"
              >
                hello@simple-iep.com
              </a>
              .
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
}
